import { useEffect, useState } from "react";
import { ZahlungsfristSettings } from "@/components/ZahlungsfristSettings";
import { useNavigate } from "react-router-dom";
import { api, DAY_KEYS, DAY_LABEL, getUser } from "@/lib/api";
import { FileImportSettings } from "@/components/FileImportSettings";
import { KalkulationSettings } from "@/components/KalkulationSettings";
import { VersandartenSettings } from "@/components/VersandartenSettings";
import { VersandMailSettings } from "@/components/VersandMailSettings";
import { SevdeskDiagnose } from "@/components/SevdeskDiagnose";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { LetterpaperPicker } from "@/components/LetterpaperPicker";
import { WebhookAttemptsSection } from "@/components/WebhookAttemptsSection";
import { IntakeLogSection } from "@/components/IntakeLogSection";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { NumberField, useFieldErrors } from "@/components/DecimalInput";
import { PERCENT_OPTS, INVALID_FORM_MSG, intOpts, formatDecimal, parseDecimal } from "@/lib/decimal";
import { SkontoRechnungFields } from "@/components/SkontoRechnungFields";
import { CheckCircle2, XCircle, Loader2, Truck, Mail, MessageSquare, ShoppingCart, FileText as FileIcon, Copy, RefreshCcw, Clock, Eye, Send, Image as ImageIcon, Trash2, Upload } from "lucide-react";

const PLACEHOLDERS = [
  { name: "Twilio SMS Service", icon: MessageSquare, desc: "SMS-Benachrichtigungen bei Statusänderungen." },
  { name: "WooCommerce Store Sync", icon: ShoppingCart, desc: "Bestellungen aus WooCommerce importieren." },
];

// Zahlenfelder der Einstellungen (Grenzen wie im Backend: skonto_prozent int 0–20)
const SKONTO_OPTS = intOpts(0, 20);
const PORT_OPTS = intOpts(1, 65535);

// Prüft ein Feld; bei Fehler → Fehler setzen + Toast und null zurück.
const checkNum = (key, text, opts, setErr) => {
  const r = parseDecimal(text, opts);
  if (r.error) { setErr((e) => ({ ...e, [key]: r.error })); toast.error(INVALID_FORM_MSG); return null; }
  return r.value;
};

export default function Einstellungen() {
  const nav = useNavigate();
  const [numErr, setNumErr, clearNumErr] = useFieldErrors();
  const [company, setCompany] = useState({ firma_name: "", ust_id: "", strasse: "", plz: "", ort: "", land: "DE", iban: "", bic: "", bank: "", email: "", telefon: "", logo_url: "" });
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInput = typeof window !== "undefined" ? { current: null } : null;
  const [stripe, setStripe] = useState({ stripe_secret_key_set: false, stripe_webhook_secret_set: false, stripe_mode: "test", stripe_secret_key_masked: "", stripe_webhook_secret_masked: "" });
  const [stripeForm, setStripeForm] = useState({ stripe_secret_key: "", stripe_webhook_secret: "", stripe_mode: "test" });
  const [savingCompany, setSavingCompany] = useState(false);
  const [savingStripe, setSavingStripe] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // SMTP
  const [smtp, setSmtp] = useState(null);
  const [smtpForm, setSmtpForm] = useState({ smtp_host: "", smtp_port: "587", smtp_user: "", smtp_password: "", smtp_use_tls: true, smtp_from_email: "", smtp_from_name: "" });
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [smtpTestEmail, setSmtpTestEmail] = useState("");
  const [smtpTestResult, setSmtpTestResult] = useState(null);
  const [testingSmtp, setTestingSmtp] = useState(false);

  // WordPress
  const [wp, setWp] = useState(null);
  const [wpSecretReveal, setWpSecretReveal] = useState("");

  // Business hours
  const [bh, setBh] = useState(null);
  const [bhStatus, setBhStatus] = useState(null);
  const [savingBh, setSavingBh] = useState(false);

  // Reminder config

  // sevDesk + DHL
  const [sevdesk, setSevdesk] = useState({ api_token_set: false, api_token_masked: "", contact_category_id: "3", default_tax_rate: 19, master_mode: true, custom_design_id: "" });
  const [sevdeskForm, setSevdeskForm] = useState({ api_token: "", contact_category_id: "3", default_tax_rate: "19", master_mode: true, custom_design_id: "" });
  const [syncLog, setSyncLog] = useState([]);
  const [syncLogFilter, setSyncLogFilter] = useState("all");
  const [testingSevdesk, setTestingSevdesk] = useState(false);
  const [sevdeskTestResult, setSevdeskTestResult] = useState(null);
  const [dhl, setDhl] = useState({ user: "", password_set: false, password_masked: "", ekp: "", billing_number_paket: "", billing_number_kleinpaket: "", billing_number_retoure: "", sandbox: true, api_key_set: false });
  const [dhlForm, setDhlForm] = useState({ user: "", password: "", ekp: "", billing_number_paket: "", billing_number_kleinpaket: "", billing_number_retoure: "", sandbox: true, api_key: "" });
  const [testingDhl, setTestingDhl] = useState(false);
  const [dhlTestResult, setDhlTestResult] = useState(null);

  useEffect(() => {
    api.get("/company").then((r) => setCompany({ ...company, ...r.data, skonto_prozent: formatDecimal(r.data.skonto_prozent ?? 3, SKONTO_OPTS) })).catch(() => {});
    api.get("/integrationen/stripe").then((r) => { setStripe(r.data); setStripeForm((f) => ({ ...f, stripe_mode: r.data.stripe_mode })); }).catch(() => {});
    api.get("/integrationen/smtp").then((r) => { setSmtp(r.data); setSmtpForm((f) => ({ ...f, smtp_host: r.data.smtp_host, smtp_port: formatDecimal(r.data.smtp_port || 587, PORT_OPTS), smtp_user: r.data.smtp_user, smtp_use_tls: r.data.smtp_use_tls, smtp_from_email: r.data.smtp_from_email, smtp_from_name: r.data.smtp_from_name })); }).catch(() => {});
    api.get("/integrationen/wordpress").then((r) => setWp(r.data)).catch(() => {});
    api.get("/business-hours").then((r) => setBh(r.data)).catch(() => {});
    api.get("/business-hours/status").then((r) => setBhStatus(r.data)).catch(() => {});
    api.get("/integrationen/sevdesk").then((r) => { setSevdesk(r.data); setSevdeskForm({ api_token: "", contact_category_id: r.data.contact_category_id, default_tax_rate: formatDecimal(r.data.default_tax_rate ?? 19, PERCENT_OPTS), master_mode: r.data.master_mode, custom_design_id: r.data.custom_design_id || "", invoice_design_id: r.data.invoice_design_id || "", tax_rule_id: r.data.tax_rule_id || "1", letterpaper_key: r.data.letterpaper_key ?? "letterpaper", letterpaper_object: r.data.letterpaper_object ?? "Letterpaper", rechnung_auto_fertigstellen: r.data.rechnung_auto_fertigstellen ?? true }); }).catch(() => {});
    api.get("/integrationen/dhl").then((r) => { setDhl(r.data); setDhlForm({ user: r.data.user, password: "", ekp: r.data.ekp, billing_number_paket: r.data.billing_number_paket || "", billing_number_kleinpaket: r.data.billing_number_kleinpaket || "", billing_number_retoure: r.data.billing_number_retoure || "", sandbox: r.data.sandbox, api_key: "" }); }).catch(() => {});
     
  }, []);

  const loadSyncLog = async () => {
    try {
      const params = {};
      if (syncLogFilter !== "all") params.status = syncLogFilter;
      const r = await api.get("/integrationen/sevdesk/sync-log", { params });
      setSyncLog(r.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { if (sevdesk.api_token_set) loadSyncLog();  }, [sevdesk.api_token_set, syncLogFilter]);

  const saveSevdesk = async () => {
    const tax = checkNum("default_tax_rate", sevdeskForm.default_tax_rate, PERCENT_OPTS, setNumErr);
    if (tax === null) return;
    try {
      const body = { ...sevdeskForm, default_tax_rate: tax };
      if (!body.api_token) delete body.api_token;
      const r = await api.put("/integrationen/sevdesk", body);
      setSevdesk(r.data); setSevdeskForm({ ...sevdeskForm, api_token: "" });
      toast.success("sevDesk gespeichert.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
  };
  const testSevdesk = async () => {
    setTestingSevdesk(true); setSevdeskTestResult(null);
    try { const r = await api.post("/integrationen/sevdesk/test"); setSevdeskTestResult(r.data); r.data.ok ? toast.success(r.data.message) : toast.error(r.data.message); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
    finally { setTestingSevdesk(false); }
  };
  const saveDhl = async () => {
    try {
      const body = { ...dhlForm };
      if (!body.password) delete body.password;
      if (!body.api_key) delete body.api_key;
      const r = await api.put("/integrationen/dhl", body);
      setDhl(r.data); setDhlForm({ ...dhlForm, password: "", api_key: "" });
      toast.success("DHL gespeichert.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
  };
  const testDhl = async () => {
    setTestingDhl(true); setDhlTestResult(null);
    try { const r = await api.post("/integrationen/dhl/test"); setDhlTestResult(r.data); r.data.ok ? toast.success(r.data.message) : toast.error(r.data.message); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
    finally { setTestingDhl(false); }
  };

  const saveCompany = async () => {
    const skonto = checkNum("skonto_prozent", company.skonto_prozent ?? "3", SKONTO_OPTS, setNumErr);
    if (skonto === null) return;
    const prefix = (company.kostenlos_angebot_prefix ?? "GA-").trim();
    if (prefix && !/^[A-Za-z0-9][A-Za-z0-9\-_/]{0,11}$/.test(prefix)) { toast.error("Präfix für kostenlose Angebote: nur Buchstaben, Ziffern, „-“, „_“ oder „/“ (max. 12 Zeichen)."); return; }
    setSavingCompany(true);
    try { await api.put("/company", { ...company, skonto_prozent: skonto, business_hours: bh }); toast.success("Firmendaten gespeichert."); }
    catch (e) { const d = e?.response?.data?.detail; toast.error(Array.isArray(d) ? (d[0]?.msg || "Speichern fehlgeschlagen.") : (d || "Speichern fehlgeschlagen.")); }
    finally { setSavingCompany(false); }
  };

  const saveStripe = async () => {
    setSavingStripe(true);
    try {
      const r = await api.put("/integrationen/stripe", stripeForm);
      setStripe(r.data);
      setStripeForm({ stripe_secret_key: "", stripe_webhook_secret: "", stripe_mode: r.data.stripe_mode });
      toast.success("Stripe-Konfiguration gespeichert.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen."); }
    finally { setSavingStripe(false); }
  };

  const testStripe = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await api.post("/integrationen/stripe/test");
      setTestResult(r.data);
      if (r.data.ok) toast.success(`Verbindung ok: ${r.data.account_name}`);
      else toast.error(r.data.message);
    } catch (e) { toast.error(e?.response?.data?.detail || "Test fehlgeschlagen."); }
    finally { setTesting(false); }
  };

  const saveSmtp = async () => {
    const port = checkNum("smtp_port", smtpForm.smtp_port, PORT_OPTS, setNumErr);
    if (port === null) return;
    setSavingSmtp(true);
    try {
      const body = { ...smtpForm, smtp_port: port };
      if (!body.smtp_password) delete body.smtp_password;
      const r = await api.put("/integrationen/smtp", body);
      setSmtp(r.data);
      setSmtpForm({ ...smtpForm, smtp_password: "" });
      toast.success("SMTP-Konfiguration gespeichert.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen."); }
    finally { setSavingSmtp(false); }
  };

  const testSmtp = async () => {
    if (!smtpTestEmail) { toast.error("Bitte Empfänger-E-Mail eingeben."); return; }
    setTestingSmtp(true); setSmtpTestResult(null);
    try {
      const r = await api.post("/integrationen/smtp/test", { to_email: smtpTestEmail });
      setSmtpTestResult(r.data);
      if (r.data.ok) toast.success(r.data.message); else toast.error(r.data.message);
    } catch (e) { toast.error(e?.response?.data?.detail || "Test fehlgeschlagen."); }
    finally { setTestingSmtp(false); }
  };

  const regenerateWp = async () => {
    if (!window.confirm("Neues Ingest-Token generieren? Das alte wird ungültig – im Plugin muss das neue Token eingetragen werden.")) return;
    try {
      const r = await api.post("/integrationen/wordpress/regenerate");
      setWp(r.data);
      const rev = await api.get("/integrationen/wordpress/reveal");
      setWpSecretReveal(rev.data.webhook_secret);
      toast.success("Neues Secret generiert. Bitte jetzt kopieren.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
  };

  const revealWp = async () => {
    try {
      const rev = await api.get("/integrationen/wordpress/reveal");
      setWpSecretReveal(rev.data.webhook_secret);
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
  };

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Kopiert."); };

  const saveWpSetting = async (patch) => {
    try {
      const r = await api.put("/integrationen/wordpress/einstellungen", patch);
      setWp(r.data);
      toast.success("Einstellung gespeichert.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen."); }
  };

  const saveBh = async () => {
    setSavingBh(true);
    try {
      await api.put("/business-hours", bh);
      const r = await api.get("/business-hours/status"); setBhStatus(r.data);
      toast.success("Geschäftszeiten gespeichert.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
    finally { setSavingBh(false); }
  };

  const updDay = (k, patch) => setBh({ ...bh, days: { ...bh.days, [k]: { ...bh.days[k], ...patch } } });

  return (
    <div className="space-y-8" data-testid="einstellungen-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Einstellungen</h1>
          <p className="text-sm text-slate-500 mt-1">Firmendaten & Integrationen konfigurieren.</p>
        </div>
        <Button variant="outline" onClick={() => nav("/einstellungen/email-warteschlange")} data-testid="email-queue-link">
          <Mail className="w-4 h-4 mr-2" /> E-Mail-Warteschlange
        </Button>
      </div>

      {/* Firmendaten */}
      <Card className="border-slate-200" data-testid="firmendaten-card">
        <CardHeader><CardTitle className="text-base">Firmendaten</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Firmenname</Label><Input value={company.firma_name} onChange={(e) => setCompany({ ...company, firma_name: e.target.value })} data-testid="company-name-input" /></div>
            <div><Label>USt-IdNr.</Label><Input value={company.ust_id} onChange={(e) => setCompany({ ...company, ust_id: e.target.value })} data-testid="company-ustid-input" /></div>
          </div>
          <div><Label>Straße</Label><Input value={company.strasse} onChange={(e) => setCompany({ ...company, strasse: e.target.value })} data-testid="company-strasse-input" /></div>
          <div className="grid grid-cols-3 gap-4">
            <div><Label>PLZ</Label><Input value={company.plz} onChange={(e) => setCompany({ ...company, plz: e.target.value })} data-testid="company-plz-input" /></div>
            <div className="col-span-2"><Label>Ort</Label><Input value={company.ort} onChange={(e) => setCompany({ ...company, ort: e.target.value })} data-testid="company-ort-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>IBAN</Label><Input value={company.iban} onChange={(e) => setCompany({ ...company, iban: e.target.value })} data-testid="company-iban-input" /></div>
            <div><Label>BIC</Label><Input value={company.bic || ""} onChange={(e) => setCompany({ ...company, bic: e.target.value })} data-testid="company-bic-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Bank</Label><Input value={company.bank || ""} onChange={(e) => setCompany({ ...company, bank: e.target.value })} data-testid="company-bank-input" /></div>
            <div><Label>Telefon</Label><Input value={company.telefon || ""} onChange={(e) => setCompany({ ...company, telefon: e.target.value })} data-testid="company-telefon-input" /></div>
          </div>
          <div><Label>E-Mail</Label><Input type="email" value={company.email} onChange={(e) => setCompany({ ...company, email: e.target.value })} data-testid="company-email-input" /></div>

          <div className="pt-2 border-t border-slate-200">
            <Label className="flex items-center gap-2"><ImageIcon className="w-4 h-4" /> Firmenlogo</Label>
            <p className="text-xs text-slate-500 mt-1 mb-3">PNG oder JPG, max. 2 MB. Wird automatisch auf 300 px Höhe skaliert und erscheint in Angebot-/Rechnung-PDF, Portal-Header und E-Mail-Header.</p>
            <div className="flex items-start gap-4">
              <div className="w-40 h-24 border border-slate-200 rounded-md bg-slate-50 flex items-center justify-center overflow-hidden" data-testid="company-logo-preview">
                {company.logo_url ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${company.logo_url}`} alt="Logo" className="max-h-full max-w-full object-contain" />
                ) : (
                  <span className="text-xs text-slate-400">Kein Logo</span>
                )}
              </div>
              <div className="space-y-2">
                <input id="logo-file-input" type="file" accept="image/png,image/jpeg" className="hidden" onChange={async (e) => {
                  const file = e.target.files?.[0]; if (!file) return;
                  if (file.size > 2 * 1024 * 1024) { toast.error("Logo überschreitet 2 MB."); return; }
                  setLogoUploading(true);
                  try {
                    const fd = new FormData(); fd.append("file", file);
                    const r = await api.post("/company/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    setCompany({ ...company, logo_url: r.data.logo_url });
                    toast.success("Logo hochgeladen.");
                  } catch (err) { toast.error(err?.response?.data?.detail || "Fehlgeschlagen."); }
                  finally { setLogoUploading(false); e.target.value = ""; }
                }} data-testid="company-logo-input" />
                <Button variant="outline" size="sm" onClick={() => document.getElementById("logo-file-input")?.click()} disabled={logoUploading} data-testid="company-logo-upload">
                  <Upload className="w-4 h-4 mr-2" /> {logoUploading ? "Lädt…" : "Logo hochladen"}
                </Button>
                {company.logo_url && (
                  <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 block" onClick={async () => {
                    if (!window.confirm("Logo entfernen?")) return;
                    try { await api.delete("/company/logo"); setCompany({ ...company, logo_url: "" }); toast.success("Logo entfernt."); }
                    catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
                  }} data-testid="company-logo-delete">
                    <Trash2 className="w-4 h-4 mr-2" /> Entfernen
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="pt-2 border-t border-slate-200 space-y-3" data-testid="company-legal-section">
            <Label className="block">Rechtliche Hinweise & Lieferzeiten</Label>
            <p className="text-xs text-slate-500">Erscheint in der Angebots-E-Mail: Lieferzeit und Annahme-Hinweis als eigene Absätze, AGB-Link dezent vor dem Firmenblock.</p>
            <div><Label>AGB-URL</Label><Input value={company.agb_url ?? ""} placeholder="https://druckexperten.com/agb/" onChange={(e) => setCompany({ ...company, agb_url: e.target.value })} data-testid="company-agb-url-input" /></div>
            <div><Label>Lieferzeit-Hinweis</Label><Textarea rows={3} value={company.lieferzeit_hinweis ?? ""} onChange={(e) => setCompany({ ...company, lieferzeit_hinweis: e.target.value })} data-testid="company-lieferzeit-input" /></div>
            <div><Label>Hinweis Annahme durch Zahlung</Label><Textarea rows={2} value={company.annahme_hinweis ?? ""} onChange={(e) => setCompany({ ...company, annahme_hinweis: e.target.value })} data-testid="company-annahme-hinweis-input" /><p className="text-xs text-slate-500 mt-1">Leer lassen, um den Hinweis in der Angebots-E-Mail auszublenden.</p></div>
            <div><Label htmlFor="company-stueckzahl-hinweis">Hinweis Stückzahlen im Angebot</Label><Textarea id="company-stueckzahl-hinweis" rows={2} value={company.stueckzahl_hinweis ?? ""} onChange={(e) => setCompany({ ...company, stueckzahl_hinweis: e.target.value })} data-testid="company-stueckzahl-hinweis-input" /><p className="text-xs text-slate-500 mt-1">Gilt für alle Angebote (manuell und Website): sevDesk-Angebot (Fußtext), CRM-Angebots-PDF und Angebots-E-Mail. Leer lassen, um den Hinweis auszublenden.</p></div>
          </div>

          <div className="pt-2 border-t border-slate-200 space-y-3" data-testid="company-kostenlos-section">
            <Label className="block">Kostenlose Lieferungen (0,00 € – Goodies/Muster)</Label>
            <p className="text-xs text-slate-500">Aufträge mit Gesamtbetrag 0,00 € (z. B. 100 % Rabatt, ohne Versandkosten) erhalten ein CRM-Angebot mit eigenem Nummernkreis statt eines sevDesk-Angebots (sevDesk unterstützt keine 0-€-Belege). Es gibt keine Zahlung und keine Rechnung.</p>
            <div className="grid sm:grid-cols-[200px_1fr] gap-3">
              <div><Label htmlFor="company-kostenlos-prefix">Nummern-Präfix</Label><Input id="company-kostenlos-prefix" className="font-mono" value={company.kostenlos_angebot_prefix ?? "GA-"} placeholder="GA-" onChange={(e) => setCompany({ ...company, kostenlos_angebot_prefix: e.target.value })} data-testid="company-kostenlos-prefix-input" /><p className="text-xs text-slate-500 mt-1">z. B. GA-00001 (fortlaufend, 5-stellig).</p></div>
              <div><Label htmlFor="company-kostenlos-hinweis">Hinweis im Angebot</Label><Textarea id="company-kostenlos-hinweis" rows={2} value={company.kostenlos_hinweis ?? ""} onChange={(e) => setCompany({ ...company, kostenlos_hinweis: e.target.value })} data-testid="company-kostenlos-hinweis-input" /><p className="text-xs text-slate-500 mt-1">Erscheint im CRM-Angebots-PDF, in der Angebots-E-Mail und im Kundenportal. Leer = kein Hinweis.</p></div>
            </div>
          </div>

          <div className="pt-2 border-t border-slate-200 space-y-3" data-testid="company-payment-section">
            <Label className="block">Zahlungsoptionen</Label>
            {[
              ["skonto_aktiv", "Skonto bei Überweisung anbieten", "company-skonto-switch"],
              ["paypal_aktiv", "PayPal in Stripe-Checkout aktivieren", "company-paypal-switch"],
              ["girocode_aktiv", "GiroCode auf Überweisungs-Panel anzeigen", "company-girocode-switch"],
              ["skonto_werbung_aktiv", "Skonto-Hinweis bewerben", "company-skonto-werbung-switch"],
            ].map(([key, label, tid]) => (
              <div key={key} className="flex items-center gap-3">
                <Switch checked={company[key] ?? true} onCheckedChange={(v) => setCompany({ ...company, [key]: v })} data-testid={tid} />
                <span className="text-sm">{label}</span>
              </div>
            ))}
            <div className="w-40"><Label htmlFor="company-skonto-prozent">Skonto in Prozent</Label><NumberField id="company-skonto-prozent" align="left" opts={SKONTO_OPTS} value={company.skonto_prozent ?? "3"} error={numErr.skonto_prozent} onChange={(t) => { setCompany((c) => ({ ...c, skonto_prozent: t })); clearNumErr("skonto_prozent"); }} testId="company-skonto-prozent-input" /></div>
            <div>
              <Label>Werbe-Text Skonto</Label>
              <Textarea rows={2} value={company.skonto_werbetext ?? ""} onChange={(e) => setCompany({ ...company, skonto_werbetext: e.target.value })} data-testid="company-skonto-werbetext-input" />
              <p className="text-xs text-slate-500 mt-1" data-testid="company-skonto-placeholder-hint">Verwenden Sie <code className="font-mono bg-slate-100 px-1 rounded">{"{skonto_prozent}"}</code> als Platzhalter, er wird automatisch ersetzt.</p>
            </div>
            <SkontoRechnungFields company={company} setCompany={setCompany} />
          </div>

          <Button onClick={saveCompany} disabled={savingCompany} data-testid="company-save-button">
            {savingCompany && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Speichern
          </Button>
        </CardContent>
      </Card>

      {/* Business Hours */}
      {bh && (
        <Card className="border-slate-200" data-testid="business-hours-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4" /> Kundenkommunikations-Zeiten</CardTitle>
            <p className="text-sm text-slate-500 mt-1">Kunden-E-Mails werden nur innerhalb dieser Zeiten versendet. Außerhalb werden sie automatisch geplant.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            {bhStatus && (
              <div className={`text-sm p-3 rounded-md border ${bhStatus.is_within_business_hours ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`} data-testid="bh-status">
                {bhStatus.is_within_business_hours ? "Aktuell innerhalb der Geschäftszeiten — Sofortversand aktiv." : `Aktuell außerhalb der Geschäftszeiten — nächster Versand um ${new Date(bhStatus.next_business_start_utc).toLocaleString("de-DE")}.`}
              </div>
            )}
            <div>
              <Label>Zeitzone</Label>
              <Input value={bh.timezone} onChange={(e) => setBh({ ...bh, timezone: e.target.value })} className="w-64" data-testid="bh-timezone-input" />
            </div>
            <div className="space-y-2">
              {DAY_KEYS.map((k) => (
                <div key={k} className="grid grid-cols-12 gap-2 items-center py-2 border-b border-slate-100 last:border-0" data-testid={`bh-day-${k}`}>
                  <div className="col-span-3 text-sm font-medium">{DAY_LABEL[k]}</div>
                  <div className="col-span-2">
                    <Switch checked={bh.days[k].enabled} onCheckedChange={(v) => updDay(k, { enabled: v })} data-testid={`bh-${k}-enabled`} />
                  </div>
                  <div className="col-span-3">
                    <Input type="time" value={bh.days[k].start} onChange={(e) => updDay(k, { start: e.target.value })} disabled={!bh.days[k].enabled} data-testid={`bh-${k}-start`} />
                  </div>
                  <div className="col-span-1 text-center text-slate-400">–</div>
                  <div className="col-span-3">
                    <Input type="time" value={bh.days[k].end} onChange={(e) => updDay(k, { end: e.target.value })} disabled={!bh.days[k].enabled} data-testid={`bh-${k}-end`} />
                  </div>
                </div>
              ))}
            </div>
            <Button onClick={saveBh} disabled={savingBh} data-testid="bh-save-button">
              {savingBh && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Zeiten speichern
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Stripe */}
      <Card className="border-slate-200" data-testid="stripe-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-base flex items-center gap-2">Stripe Zahlungs-Integration
              {stripe.stripe_secret_key_set ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Aktiv</Badge> : <Badge variant="outline">Nicht konfiguriert</Badge>}
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">Keys werden verschlüsselt gespeichert und nie im Klartext ausgeliefert.</p>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Secret Key</Label>
            <Input type="password" placeholder={stripe.stripe_secret_key_set ? `Aktuell: ${stripe.stripe_secret_key_masked}` : "sk_test_… oder sk_live_…"}
              value={stripeForm.stripe_secret_key} onChange={(e) => setStripeForm({ ...stripeForm, stripe_secret_key: e.target.value })} data-testid="stripe-secret-input" />
          </div>
          <div>
            <Label>Webhook Secret</Label>
            <Input type="password" placeholder={stripe.stripe_webhook_secret_set ? `Aktuell: ${stripe.stripe_webhook_secret_masked}` : "whsec_…"}
              value={stripeForm.stripe_webhook_secret} onChange={(e) => setStripeForm({ ...stripeForm, stripe_webhook_secret: e.target.value })} data-testid="stripe-webhook-input" />
          </div>
          <div>
            <Label>Modus</Label>
            <Select value={stripeForm.stripe_mode} onValueChange={(v) => setStripeForm({ ...stripeForm, stripe_mode: v })}>
              <SelectTrigger className="w-40" data-testid="stripe-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="test">Test</SelectItem><SelectItem value="live">Live</SelectItem></SelectContent>
            </Select>
          </div>
          <p className="text-xs text-slate-500" data-testid="stripe-paypal-hint">Hinweis: PayPal muss in deinem Stripe-Dashboard unter Settings → Payment Methods aktiviert sein. Ist es dort nicht aktiv, bietet der Checkout automatisch nur Kartenzahlung an.</p>
          <div className="flex gap-2">
            <Button onClick={saveStripe} disabled={savingStripe} data-testid="stripe-save-button">
              {savingStripe && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Speichern
            </Button>
            <Button variant="outline" onClick={testStripe} disabled={testing || !stripe.stripe_secret_key_set} data-testid="stripe-test-connection-button">
              {testing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Verbindung testen
            </Button>
          </div>
          {testResult && (
            <div className={`flex items-center gap-2 text-sm p-3 rounded-md border ${testResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-800"}`} data-testid="stripe-test-result">
              {testResult.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
              {testResult.ok ? `Verbunden mit: ${testResult.account_name}` : testResult.message}
            </div>
          )}
        </CardContent>
      </Card>

      {/* SMTP */}
      {smtp && (
        <Card className="border-slate-200" data-testid="smtp-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Mail className="w-4 h-4" /> SMTP (E-Mail-Versand)
              {smtp.smtp_password_set && smtp.smtp_host ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Aktiv</Badge> : <Badge variant="outline">Nicht konfiguriert</Badge>}
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">SMTP-Zugangsdaten werden verschlüsselt gespeichert.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2"><Label>Host</Label><Input value={smtpForm.smtp_host} onChange={(e) => setSmtpForm({ ...smtpForm, smtp_host: e.target.value })} placeholder="smtp.example.com" data-testid="smtp-host-input" /></div>
              <div><Label htmlFor="smtp-port">Port</Label><NumberField id="smtp-port" align="left" opts={PORT_OPTS} value={smtpForm.smtp_port} error={numErr.smtp_port} onChange={(t) => { setSmtpForm((f) => ({ ...f, smtp_port: t })); clearNumErr("smtp_port"); }} testId="smtp-port-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Benutzername</Label><Input value={smtpForm.smtp_user} onChange={(e) => setSmtpForm({ ...smtpForm, smtp_user: e.target.value })} data-testid="smtp-user-input" /></div>
              <div><Label>Passwort</Label><Input type="password" placeholder={smtp.smtp_password_set ? `Aktuell: ${smtp.smtp_password_masked}` : "••••••••"} value={smtpForm.smtp_password} onChange={(e) => setSmtpForm({ ...smtpForm, smtp_password: e.target.value })} data-testid="smtp-password-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Absender-E-Mail</Label><Input type="email" value={smtpForm.smtp_from_email} onChange={(e) => setSmtpForm({ ...smtpForm, smtp_from_email: e.target.value })} data-testid="smtp-from-email-input" /></div>
              <div><Label>Absender-Name</Label><Input value={smtpForm.smtp_from_name} onChange={(e) => setSmtpForm({ ...smtpForm, smtp_from_name: e.target.value })} data-testid="smtp-from-name-input" /></div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={smtpForm.smtp_use_tls} onCheckedChange={(v) => setSmtpForm({ ...smtpForm, smtp_use_tls: v })} data-testid="smtp-tls-switch" />
              <Label>STARTTLS verwenden (Port 465 = implizites TLS, sonst STARTTLS)</Label>
            </div>
            <div className="flex gap-2">
              <Button onClick={saveSmtp} disabled={savingSmtp} data-testid="smtp-save-button">
                {savingSmtp && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Speichern
              </Button>
            </div>
            <div className="border-t pt-4 space-y-2">
              <Label>Test-E-Mail senden an</Label>
              <div className="flex gap-2">
                <Input type="email" placeholder="ihre@email.de" value={smtpTestEmail} onChange={(e) => setSmtpTestEmail(e.target.value)} data-testid="smtp-test-email-input" />
                <Button variant="outline" onClick={testSmtp} disabled={testingSmtp} data-testid="smtp-test-button">
                  {testingSmtp && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} <Send className="w-4 h-4 mr-2" /> Test senden
                </Button>
              </div>
              {smtpTestResult && (
                <div className={`flex items-center gap-2 text-sm p-3 rounded-md border ${smtpTestResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-800"}`} data-testid="smtp-test-result">
                  {smtpTestResult.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                  {smtpTestResult.message}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* WordPress */}
      <VersandartenSettings canEdit={["admin", "manager"].includes(getUser()?.rolle)} />
      <VersandMailSettings canEdit={["admin", "manager"].includes(getUser()?.rolle)} />
      {wp && (
        <Card className="border-slate-200" data-testid="wordpress-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ShoppingCart className="w-4 h-4" /> Website-Anbindung (Druckexperten Kalkulator)
              {wp.webhook_secret_set ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Aktiv</Badge> : <Badge variant="outline">Nicht konfiguriert</Badge>}
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">Im WordPress-Plugin „Druckexperten Kalkulator“ die Endpunkt-URL und das Ingest-Token eintragen.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Plugin-Endpunkt</Label>
              <div className="flex gap-2">
                <Input readOnly value={wp.ingest_url} className="font-mono text-xs" data-testid="wp-ingest-url-input" />
                <Button variant="outline" onClick={() => copy(wp.ingest_url)} data-testid="wp-ingest-url-copy"><Copy className="w-4 h-4" /></Button>
              </div>
            </div>
            <div>
              <Label>Ingest-Token</Label>
              <div className="flex gap-2">
                <Input readOnly value={wpSecretReveal || (wp.webhook_secret_set ? wp.webhook_secret_masked : "")} className="font-mono text-xs" data-testid="wp-secret-input" />
                {wp.webhook_secret_set && !wpSecretReveal && <Button variant="outline" onClick={revealWp} data-testid="wp-secret-reveal"><Eye className="w-4 h-4" /></Button>}
                {wpSecretReveal && <Button variant="outline" onClick={() => copy(wpSecretReveal)} data-testid="wp-secret-copy"><Copy className="w-4 h-4" /></Button>}
              </div>
              {wpSecretReveal && <p className="text-xs text-amber-700 mt-1">Bitte jetzt kopieren – nach Neuladen der Seite nur wieder mit Reveal-Button.</p>}
            </div>
            <Button variant="outline" onClick={regenerateWp} data-testid="wp-regenerate-button"><RefreshCcw className="w-4 h-4 mr-2" /> {wp.webhook_secret_set ? "Neu generieren" : "Token generieren"}</Button>
            <div className="space-y-3 border-t border-slate-100 pt-4">
              <label className="flex items-center justify-between gap-4 text-sm">
                <span>Website-Anfragen automatisch als Auftrag anlegen<span className="block text-xs text-slate-500">Kunde wird per E-Mail gefunden oder angelegt; Position aus der Kalkulation. Preisfreigabe bleibt manuell.</span></span>
                <Switch checked={wp.website_auto_order} onCheckedChange={(v) => saveWpSetting({ website_auto_order: v })} data-testid="wp-auto-order-switch" />
              </label>
              <label className="flex items-center justify-between gap-4 text-sm">
                <span>Eingangsbestätigung an Kunden bei Website-Anfragen senden<span className="block text-xs text-slate-500">Standard aus – das Plugin versendet bereits eine eigene Bestätigung. Interne Benachrichtigungen bleiben aktiv.</span></span>
                <Switch checked={wp.website_customer_confirmation} onCheckedChange={(v) => saveWpSetting({ website_customer_confirmation: v })} data-testid="wp-customer-confirmation-switch" />
              </label>
            </div>
            <KalkulationSettings isAdmin={getUser()?.rolle === "admin"} />
            <FileImportSettings isAdmin={getUser()?.rolle === "admin"} />
            <IntakeLogSection />
            <details className="border border-slate-200 rounded-md p-3 text-sm" data-testid="wp-hmac-alternative">
              <summary className="cursor-pointer text-slate-600">Alternative: HMAC-Webhook (z. B. eigene Formulare)</summary>
              <div className="mt-3 space-y-3">
              <div className="flex gap-2">
                <Input readOnly value={wp.webhook_url} className="font-mono text-xs" data-testid="wp-url-input" />
                <Button variant="outline" onClick={() => copy(wp.webhook_url)} data-testid="wp-url-copy"><Copy className="w-4 h-4" /></Button>
              </div>
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs font-mono text-slate-700 overflow-auto">
              <div className="font-semibold mb-1 text-slate-600 non-mono not-italic">Beispiel-cURL:</div>
              curl -X POST {wp.webhook_url} \<br />
              &nbsp;&nbsp;-H "Content-Type: application/json" \<br />
              &nbsp;&nbsp;-H "X-DE-Signature: sha256=$(echo -n '{"{"}nachricht":"Hallo"{"}"}' | openssl dgst -sha256 -hmac 'SECRET' | awk '{"{"}print $2{"}"}')" \<br />
              &nbsp;&nbsp;-d '{"{"}"name":"Max Mustermann","email":"max@example.com","nachricht":"3D-Druck Anfrage"{"}"}'
            </div>
              </div>
            </details>
            <WebhookAttemptsSection />
          </CardContent>
        </Card>
      )}

      {/* Placeholders */}
      <ZahlungsfristSettings />

      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-3">Weitere Integrationen</h2>
        <div className="space-y-4 mb-4">
          <Card className="border-slate-200" data-testid="sevdesk-card">
            <CardHeader><CardTitle className="text-base flex items-center gap-2">sevDesk Buchhaltung {sevdesk.api_token_set ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Aktiv</Badge> : <Badge variant="outline">Nicht konfiguriert</Badge>}</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div><Label>API-Token</Label><Input type="password" placeholder={sevdesk.api_token_set ? `Aktuell: ${sevdesk.api_token_masked}` : "sevDesk Token"} value={sevdeskForm.api_token} onChange={(e) => setSevdeskForm({ ...sevdeskForm, api_token: e.target.value })} data-testid="sevdesk-token-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Contact-Kategorie ID</Label><Input value={sevdeskForm.contact_category_id} onChange={(e) => setSevdeskForm({ ...sevdeskForm, contact_category_id: e.target.value })} data-testid="sevdesk-category-input" /></div>
                <div><Label htmlFor="sevdesk-tax-rate">MwSt (%)</Label><NumberField id="sevdesk-tax-rate" align="left" opts={PERCENT_OPTS} value={sevdeskForm.default_tax_rate} error={numErr.default_tax_rate} onChange={(t) => { setSevdeskForm((f) => ({ ...f, default_tax_rate: t })); clearNumErr("default_tax_rate"); }} testId="sevdesk-tax-rate-input" /></div>
                <div><Label>Steuerregel-ID (sevDesk Update 2.0)</Label><Input value={sevdeskForm.tax_rule_id || "1"} onChange={(e) => setSevdeskForm({ ...sevdeskForm, tax_rule_id: e.target.value })} data-testid="sevdesk-taxrule-input" /><p className="text-[11px] text-slate-500 mt-0.5">1 = umsatzsteuerpflichtig, 11 = Kleinunternehmer</p></div>
              </div>
              <LetterpaperPicker label="Briefpapier Angebote" value={sevdeskForm.custom_design_id} onChange={(v) => setSevdeskForm({ ...sevdeskForm, custom_design_id: v })} disabled={!sevdesk.api_token_set} />
              <LetterpaperPicker label="Briefpapier Rechnungen" testId="sevdesk-invoice-letterpaper" emptyLabel="Wie Angebote" value={sevdeskForm.invoice_design_id}
                onChange={(v) => setSevdeskForm({ ...sevdeskForm, invoice_design_id: v })} disabled={!sevdesk.api_token_set}
                hint="Wird beim Erstellen jeder Rechnung zuletzt per changeParameter gesetzt (zweimal), danach wird das PDF geladen. Leer = gleiches Briefpapier wie Angebote." />
              <div className="flex items-start gap-3 p-3 border border-slate-200 rounded-md" data-testid="sevdesk-auto-fertigstellen">
                <Switch id="sevdesk-auto-fertigstellen" checked={sevdeskForm.rechnung_auto_fertigstellen !== false} onCheckedChange={(v) => setSevdeskForm({ ...sevdeskForm, rechnung_auto_fertigstellen: v })} data-testid="sevdesk-auto-fertigstellen-switch" />
                <div className="flex-1">
                  <Label htmlFor="sevdesk-auto-fertigstellen">Automatisch erstellte Rechnungen direkt fertigstellen</Label>
                  <p className="text-xs text-slate-500 mt-0.5">Rechnungen werden in sevDesk auf „Offen“ gesetzt (finale Nummer, buchbar). Beim Versand an den Kunden (Versandmitteilung mit Rechnung) wird immer fertiggestellt; „Rechnung erstellen“ am Auftrag fragt jeweils nach.</p>
                </div>
              </div>
              <details className="border border-slate-200 rounded-md p-3 text-sm" data-testid="sevdesk-expert-mode">
                <summary className="cursor-pointer text-slate-600">Expertenmodus: Briefpapier-Feld</summary>
                <p className="text-xs text-slate-500 mt-2">Wie im WordPress-Plugin: Das Briefpapier wird als Feld im Angebot/in der Rechnung gesendet. Nur ändern, wenn sevDesk andere Feldnamen erwartet.</p>
                <div className="grid sm:grid-cols-2 gap-3 mt-2">
                  <div><Label>Feldname</Label><Input value={sevdeskForm.letterpaper_key ?? "letterpaper"} onChange={(e) => setSevdeskForm({ ...sevdeskForm, letterpaper_key: e.target.value })} data-testid="sevdesk-letterpaper-key-input" /></div>
                  <div><Label>objectName (leer = nur ID)</Label><Input value={sevdeskForm.letterpaper_object ?? "Letterpaper"} onChange={(e) => setSevdeskForm({ ...sevdeskForm, letterpaper_object: e.target.value })} data-testid="sevdesk-letterpaper-object-input" /></div>
                </div>
              </details>
              <div className="flex items-start gap-3 p-3 border border-blue-200 bg-blue-50 rounded-md">
                <Switch checked={!!sevdeskForm.master_mode} onCheckedChange={(v) => setSevdeskForm({ ...sevdeskForm, master_mode: v })} data-testid="sevdesk-master-switch" />
                <div className="flex-1">
                  <Label className="text-blue-900">sevDesk als Dokumenten-Master nutzen</Label>
                  <p className="text-xs text-blue-700 mt-0.5">Wenn aktiv, werden Angebots- und Rechnungs-PDFs in sevDesk erzeugt (mit dortigem Briefpapier) und als lokale Kopie gespeichert. Fällt sevDesk aus, greift automatisch das lokale Fallback-PDF.</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={saveSevdesk} data-testid="sevdesk-save">Speichern</Button>
                <Button variant="outline" onClick={testSevdesk} disabled={testingSevdesk || !sevdesk.api_token_set} data-testid="sevdesk-test">{testingSevdesk && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Verbindung testen</Button>
              </div>
              {sevdeskTestResult && <div className={`text-sm p-2 rounded border ${sevdeskTestResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-800"}`} data-testid="sevdesk-test-result">{sevdeskTestResult.message}</div>}
              {sevdesk.api_token_set && getUser()?.rolle === "admin" && <SevdeskDiagnose />}
              <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-2" data-testid="sevdesk-numbering-hint">
                Falls Angebote nicht erzeugt werden können, prüfe in sevDesk unter Einstellungen → Nummernkreise, ob „Angebot“ (AN) angelegt ist und ein freies Format hat.
              </p>
              {sevdesk.api_token_set && (
                <div className="pt-4 border-t border-slate-200" data-testid="sevdesk-sync-log">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm">Sync-Log (letzte Einträge)</Label>
                    <div className="flex gap-1">
                      {["all", "ok", "error"].map((k) => (
                        <button
                          key={k}
                          onClick={() => setSyncLogFilter(k)}
                          data-testid={`sevdesk-sync-filter-${k}`}
                          className={`px-2 py-0.5 rounded text-xs border ${syncLogFilter === k ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                        >
                          {k === "all" ? "Alle" : k === "ok" ? "OK" : "Fehler"}
                        </button>
                      ))}
                      <button onClick={loadSyncLog} className="px-2 py-0.5 text-xs text-slate-500 hover:text-slate-800" data-testid="sevdesk-sync-reload">↻</button>
                    </div>
                  </div>
                  <div className="max-h-56 overflow-y-auto border border-slate-200 rounded">
                    {syncLog.length === 0 ? (
                      <div className="p-4 text-center text-xs text-slate-500 italic">Noch keine Sync-Einträge.</div>
                    ) : syncLog.map((e) => (
                      <div key={e.id} className="flex items-start gap-2 px-3 py-2 border-b border-slate-100 last:border-0 text-xs" data-testid={`sevdesk-sync-row-${e.id}`}>
                        <span className={`shrink-0 w-1.5 h-1.5 rounded-full mt-1.5 ${e.ok ? "bg-emerald-500" : "bg-rose-500"}`} />
                        <div className="flex-1 min-w-0">
                          <div className="font-mono text-slate-700">{e.action}{e.sevdesk_id ? ` · #${e.sevdesk_id}` : ""}{e.http_status ? ` · ${e.http_status}` : ""}</div>
                          <div className="text-slate-500 truncate">{e.message || "OK"}</div>
                          {e.raw_request && (
                            <details className="mt-1" data-testid={`sevdesk-sync-raw-${e.id}`}>
                              <summary className="cursor-pointer text-slate-400">Raw Request</summary>
                              <Button size="sm" variant="outline" className="h-6 text-xs mt-1" onClick={() => { let t = e.raw_request; try { t = JSON.stringify(JSON.parse(t), null, 2); } catch { /* roh */ } navigator.clipboard.writeText(t); toast.success("Kopiert."); }} data-testid={`sevdesk-sync-raw-copy-${e.id}`}>Kopieren</Button>
                              <pre className="whitespace-pre-wrap break-all bg-slate-50 p-2 rounded mt-1 max-h-48 overflow-auto">{e.raw_request}</pre>
                            </details>
                          )}
                        </div>
                        <div className="shrink-0 text-slate-400 font-mono">{(e.at || "").slice(11, 19)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200" data-testid="dhl-card">
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Truck className="w-4 h-4" /> DHL Versandmarken {dhl.password_set ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Aktiv</Badge> : <Badge variant="outline">Nicht konfiguriert</Badge>}</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>DHL Benutzer</Label><Input value={dhlForm.user} onChange={(e) => setDhlForm({ ...dhlForm, user: e.target.value })} data-testid="dhl-user-input" /></div>
                <div><Label>DHL Passwort</Label><Input type="password" placeholder={dhl.password_set ? `Aktuell: ${dhl.password_masked}` : "••••"} value={dhlForm.password} onChange={(e) => setDhlForm({ ...dhlForm, password: e.target.value })} data-testid="dhl-password-input" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>EKP-Nummer</Label><Input value={dhlForm.ekp} onChange={(e) => setDhlForm({ ...dhlForm, ekp: e.target.value.replace(/\D/g,"") })} maxLength={10} placeholder="10-stellig" data-testid="dhl-ekp-input" /></div>
                <div><Label>Abr. Paket <span className="text-xs text-slate-400">(DHL PAKET GKP)</span></Label><Input value={dhlForm.billing_number_paket} onChange={(e) => setDhlForm({ ...dhlForm, billing_number_paket: e.target.value.replace(/\D/g,"") })} maxLength={14} placeholder="z.B. 200101" data-testid="dhl-billing-paket-input" /></div>
                <div><Label>Abr. Kleinpaket <span className="text-xs text-slate-400">(V62KP)</span></Label><Input value={dhlForm.billing_number_kleinpaket} onChange={(e) => setDhlForm({ ...dhlForm, billing_number_kleinpaket: e.target.value.replace(/\D/g,"") })} maxLength={14} placeholder="z.B. 206201" data-testid="dhl-billing-kleinpaket-input" /></div>
              </div>
              <div>
                <Label>Abr. Retoure <span className="text-xs text-slate-400">(DHL RETOURE ONLINE)</span></Label>
                <Input value={dhlForm.billing_number_retoure} onChange={(e) => setDhlForm({ ...dhlForm, billing_number_retoure: e.target.value.replace(/\D/g,"") })} maxLength={14} placeholder="z.B. 200701" data-testid="dhl-billing-retoure-input" />
                <p className="text-xs text-slate-500 mt-1">Verfahren + Teilnehmer (meist 6 Ziffern) oder vollständige Billing-ID (bis 14 Ziffern).</p>
              </div>
              <div>
                <Label>DHL API-Key <span className="text-xs text-rose-600">(Developer Portal, Pflicht)</span></Label>
                <Input type="password" placeholder={dhl.api_key_set ? "gesetzt — zum Ändern neu eingeben" : "API-Key eintragen"} value={dhlForm.api_key} onChange={(e) => setDhlForm({ ...dhlForm, api_key: e.target.value })} data-testid="dhl-apikey-input" />
                <p className="text-xs text-slate-500 mt-1">Erstelle auf <a href="https://developer.dhl.com" target="_blank" rel="noreferrer" className="underline">developer.dhl.com</a> eine App „Parcel DE Shipping" und trage hier den API-Key ein. Für Sandbox-Modus kann derselbe Key verwendet werden.</p>
              </div>
              <div className="flex items-center gap-3"><Switch checked={dhlForm.sandbox} onCheckedChange={(v) => setDhlForm({ ...dhlForm, sandbox: v })} data-testid="dhl-sandbox-switch" /><span className="text-sm">Sandbox-Modus</span></div>
              <div className="flex gap-2">
                <Button onClick={saveDhl} data-testid="dhl-save">Speichern</Button>
                <Button variant="outline" onClick={testDhl} disabled={testingDhl || !dhl.password_set || !dhl.api_key_set} title={!dhl.api_key_set ? "API-Key fehlt" : ""} data-testid="dhl-test">{testingDhl && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Verbindung testen</Button>
              </div>
              {dhlTestResult && <div className={`text-sm p-2 rounded border ${dhlTestResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-800"}`} data-testid="dhl-test-result">{dhlTestResult.message}</div>}
            </CardContent>
          </Card>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {PLACEHOLDERS.map((p) => (
            <Card key={p.name} className="border-slate-200 opacity-70" data-testid={`placeholder-${p.name}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-md bg-slate-100 flex items-center justify-center text-slate-500"><p.icon className="w-5 h-5" /></div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-slate-900">{p.name}</div>
                    <div className="text-xs text-slate-500 mt-1">{p.desc}</div>
                    <Badge variant="outline" className="mt-2 text-xs">Demnächst verknüpfbar</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
